import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import API_CONFIG from '../config/api';

interface TestRequest {
  _id: string;
  displayId?: number; // Display ID for tracking (شناسه)
  patientInfo: {
    patientType: string;
    firstName: string;
    lastName: string;
    nationalId: string;
    mobile: string;
    gender: string;
  };
  samplingLocation: {
    type: string; // 'home' or 'lab'
    needsContainer?: boolean;
    freeTransportation?: boolean;
  };
  addressInfo: {
    region: string;
    field: string;
    street: string;
    alley?: string;
    building?: string;
    unit?: string;
    number?: string;
  };
  ageInfo: {
    age: number;
  };
  status: string;
  createdAt: string;
  updatedAt: string;
  rejectionReason?: string;
  instructions?: string;
  editDeadline?: string;
  editVersion?: number;
  labResponseDateTime?: string; // Date and time when lab responds (approval time)
  transaction?: {
    _id: string;
    amount: number;
    status: 'pending' | 'completed' | 'failed';
    refId?: string;
    paymentDate?: string;
    gateway?: string;
    createdAt: string;
    testRequestId: string;
    userId: string;
  };
  paymentExtensionRequest?: boolean;
  paymentExtensionStatus?: 'pending' | 'approved' | 'rejected';
  paymentExtensionRejectionReason?: string;
  samplingDate?: string; // Sampling date
  samplingTime?: string; // Sampling time
  amount?: number; // Amount to be paid
  transportationPrice?: number; // Transportation price (هزینه ایاب و ذهاب)
  freeTransportation?: boolean; // Free transportation flag
}

interface PaginationMeta {
  currentPage: number;
  pages: number;
  count: number;
}

// Simple function to convert Gregorian date to Jalali (Shamsi)
const toShamsiDate = (date: string | undefined): string => {
  if (!date) return '---';

  // YYYY-MM-DD must parse as local calendar day (avoid UTC off-by-one)
  const d = /^\d{4}-\d{2}-\d{2}$/.test(date)
    ? (() => {
        const [y, m, day] = date.split('-').map(Number);
        return new Date(y, m - 1, day, 12, 0, 0);
      })()
    : new Date(date);

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    calendar: 'persian'
  };

  return new Intl.DateTimeFormat('fa-IR', options).format(d);
};

// Function to format time
const formatTime = (date: string | undefined): string => {
  if (!date) return '---';

  const d = new Date(date);
  return d.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' });
};

// Function to translate status to Persian
const translateStatus = (status: string, request?: TestRequest): string => {
  if (status === 'paid') {
    // Check if total amount (base + transportation) is 0 OR no transaction exists
    if (request) {
      const baseAmount = request.amount || 0;
      const transportationAmount = request.transportationPrice || 0;
      const totalAmount = baseAmount + transportationAmount;

      if (totalAmount === 0 || !request.transaction) {
        return 'نهایی شده';
      }
    }
    return 'نهایی و پرداخت شده';
  }

  const statusMap: Record<string, string> = {
    incomplete: 'ناتمام',
    canceled: 'لغو شده',
    reviewing: 'در حال بررسی',
    approved: 'تایید شده',
    rejected: 'تایید نشده',
    edited: 'ویرایش شده',
    expired: 'منقضی شده'
  };

  return statusMap[status] || status;
};

// Function to translate patient type to Persian
const translatePatientType = (type: string): string => {
  const typeMap: Record<string, string> = {
    self: 'خودم',
    child: 'فرزند',
    spouse: 'همسر',
    father: 'پدر',
    mother: 'مادر',
    other: 'سایر'
  };

  return typeMap[type] || type;
};

// Function to calculate remaining payment time (90 minutes from approval)
const calculateRemainingPaymentTime = (labResponseDateTime: string | undefined, status: string): string => {
  if (status !== 'approved' || !labResponseDateTime) return '---';

  const approvalTime = new Date(labResponseDateTime);
  const now = new Date();
  const deadline = new Date(approvalTime.getTime() + 90 * 60 * 1000); // 90 minutes after lab approval

  const diffMs = deadline.getTime() - now.getTime();

  if (diffMs <= 0) return 'منقضی شده';

  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  if (hours > 0) {
    return `${hours} ساعت و ${minutes} دقیقه`;
  } else {
    return `${minutes} دقیقه`;
  }
};

// Function to check if payment deadline has passed
const isPaymentDeadlinePassed = (labResponseDateTime: string | undefined, status: string): boolean => {
  if (status !== 'expired' || !labResponseDateTime) return false;

  const approvalTime = new Date(labResponseDateTime);
  const now = new Date();
  const deadline = new Date(approvalTime.getTime() + 90 * 60 * 1000); // 90 minutes after lab approval

  return now.getTime() > deadline.getTime();
};

// Function to calculate remaining edit time for rejected requests
const calculateRemainingEditTime = (editDeadline: string | undefined, status: string): string => {
  if (status !== 'rejected' || !editDeadline) return '---';

  const deadline = new Date(editDeadline);
  const now = new Date();
  const diffMs = deadline.getTime() - now.getTime();

  if (diffMs <= 0) return 'منقضی شده';

  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (diffHours > 0) {
    return `${diffHours} ساعت و ${diffMinutes} دقیقه`;
  } else {
    return `${diffMinutes} دقیقه`;
  }
};

// Function to check if user can edit a rejected request
const canEditRequest = (editDeadline: string | undefined, status: string): boolean => {
  if (status !== 'rejected' || !editDeadline) return false;

  const deadline = new Date(editDeadline);
  const now = new Date();
  return now.getTime() <= deadline.getTime();
};

// Function to get payment extension status display
const getPaymentExtensionStatusDisplay = (paymentExtensionRequest: boolean | undefined, paymentExtensionStatus?: string, rejectionReason?: string) => {
  // Show if there was ever a payment extension request (regardless of current paymentExtensionRequest status)
  if (!paymentExtensionStatus) return null;

  switch (paymentExtensionStatus) {
    case 'approved':
      return { text: '✅ تایید شده', className: 'bg-green-100 text-green-700', reason: null };
    case 'rejected':
      return { text: '❌ رد شده', className: 'bg-red-100 text-red-700', reason: rejectionReason };
    default:
      return { text: '⏳ در انتظار تایید', className: 'bg-blue-100 text-blue-700', reason: null };
  }
};

// Function to check if user can still request payment extension (within 24 hours of approval)
const canRequestExtension = (labResponseDateTime: string | undefined, status: string): boolean => {
  // Can only request extension for expired requests
  if (status !== 'expired') return false;

  // If no approval time, cannot request extension
  if (!labResponseDateTime) return false;

  const approvalTime = new Date(labResponseDateTime);
  const now = new Date();
  const twentyFourHoursMs = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
  const timeSinceApproval = now.getTime() - approvalTime.getTime();

  // Can only request extension within 24 hours of approval
  return timeSinceApproval < twentyFourHoursMs;
};

const MyTests: React.FC = () => {
  const [testRequests, setTestRequests] = useState<TestRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [pagination, setPagination] = useState<PaginationMeta>({
    currentPage: 1,
    pages: 1,
    count: 0
  });
  const [requestingExtension, setRequestingExtension] = useState<string | null>(null);
  const [addressModalOpen, setAddressModalOpen] = useState<boolean>(false);
  const [selectedAddress, setSelectedAddress] = useState<TestRequest['addressInfo'] | null>(null);

  // Function to fetch test requests
  const fetchTestRequests = async (page = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');

      if (!token) {
        setError('لطفا ابتدا وارد حساب کاربری خود شوید');
        setLoading(false);
        return;
      }

      const response = await fetch(API_CONFIG.getUrl(`test-requests?page=${page}&limit=10`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('خطا در دریافت اطلاعات');
      }

      const data = await response.json();

      // Debug: Log transaction data to console

      setTestRequests(data.data);
      setPagination({
        currentPage: data.currentPage,
        pages: data.pages,
        count: data.count
      });
    } catch (err) {
      setError('خطا در دریافت اطلاعات درخواست‌های آزمایش');

    } finally {
      setLoading(false);
    }
  };

  // Fetch test requests on component mount
  useEffect(() => {
    fetchTestRequests();
  }, []);

  // Function to handle pagination
  const handlePageChange = (page: number) => {
    if (page < 1 || page > pagination.pages) return;
    fetchTestRequests(page);
  };

  // Function to refresh current page (useful when new requests are created)
  const refreshCurrentPage = () => {
    fetchTestRequests(pagination.currentPage);
  };

  // Function to request payment extension
  const handleRequestPaymentExtension = async (requestId: string) => {
    try {
      setRequestingExtension(requestId);
      const token = localStorage.getItem('authToken');

      if (!token) {
        setError('لطفا ابتدا وارد حساب کاربری خود شوید.');
        return;
      }

      const response = await fetch(`${API_CONFIG.baseURL}/test-requests/${requestId}/request-payment-extension`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Update the local state to reflect the change
          setTestRequests(prevRequests =>
            prevRequests.map(req =>
              req._id === requestId
                ? { ...req, paymentExtensionRequest: true }
                : req
            )
          );
          alert('درخواست تمدید پرداخت با موفقیت ارسال شد');
        } else {
          setError(data.message || 'خطا در ارسال درخواست تمدید پرداخت');
        }
      } else {
        setError('خطا در ارتباط با سرور');
      }
    } catch (err) {

      setError('خطا در ارتباط با سرور');
    } finally {
      setRequestingExtension(null);
    }
  };

  // Function to format address
  const formatAddress = (addressInfo: TestRequest['addressInfo']): string => {
    const parts = [];
    if (addressInfo.region) parts.push(addressInfo.region);
    if (addressInfo.field) parts.push(addressInfo.field);
    if (addressInfo.street) parts.push(addressInfo.street);
    if (addressInfo.alley) parts.push(addressInfo.alley);
    if (addressInfo.building) parts.push(addressInfo.building);
    if (addressInfo.unit) parts.push(`واحد ${addressInfo.unit}`);
    if (addressInfo.number) parts.push(`پلاک ${addressInfo.number}`);

    return parts.join('، ');
  };

  // Function to format sampling location
  const formatSamplingLocation = (request: TestRequest): React.ReactNode => {
    if (request.samplingLocation?.type === 'lab') {
      return (
        <div className="text-xs">
          <div>نمونه گیری در آزمایشگاه</div>
        </div>
      );
    } else {
      return (
        <div className="text-xs space-y-1">
          <div>نمونه گیری در خانه</div>
          {request.samplingLocation?.type === 'home' && (
            <div className="space-y-0.5">
              <div className="flex items-center text-xs">
                <span className="ml-1">
                  {request.samplingLocation.needsContainer ? '✓' : '✗'}
                </span>
                <span className={request.samplingLocation.needsContainer ? 'text-green-600' : 'text-red-600'}>
                  ظرف
                </span>
              </div>
              <div className="flex items-center text-xs">
                <span className="ml-1">
                  {request.samplingLocation.freeTransportation ? '✓' : '✗'}
                </span>
                <span className={request.samplingLocation.freeTransportation ? 'text-green-600' : 'text-red-600'}>
                  رایگان
                </span>
              </div>
            </div>
          )}
          {/* Display abbreviated address information for home sampling */}
          {request.samplingLocation?.type === 'home' && request.addressInfo && (
            <div className="mt-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedAddress(request.addressInfo);
                  setAddressModalOpen(true);
                }}
                className="text-right p-2 bg-blue-50 hover:bg-blue-100 rounded text-xs text-blue-700 transition-colors w-full"
              >
                <div className="font-medium mb-1">آدرس: (کلیک برای مشاهده کامل)</div>
                <div className="text-gray-600 truncate">
                  {request.addressInfo.region && `منطقه: ${request.addressInfo.region}`}
                </div>
              </button>
            </div>
          )}
        </div>
      );
    }
  };

  return (
    <DashboardLayout>
      <div className="mx-auto px-4 py-8">
        <div className="mx-auto bg-white rounded-md border border-gray-200">
          <div className="p-6">
            <div className="flex justify-between items-center mb-8">
              <h1 className="text-2xl font-bold text-gray-800">
                آزمایش‌های من
              </h1>
              <button
                onClick={refreshCurrentPage}
                className="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 transition-colors flex items-center gap-2"
                disabled={loading}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                به‌روزرسانی
              </button>
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
              </div>
            ) : error ? (
              <div className="text-center text-red-500 py-10">
                <p>{error}</p>
                <button
                  onClick={() => fetchTestRequests()}
                  className="mt-4 px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 transition-colors"
                >
                  تلاش مجدد
                </button>
              </div>
            ) : testRequests.length === 0 ? (
              <div className="text-center text-gray-500 py-10">
                <p>هیچ درخواست آزمایشی یافت نشد.</p>
              </div>
            ) : (
              <>
                {/* Main Table with Scrollbar */}
                <div className="overflow-x-auto border border-gray-200 rounded custom-scrollbar" style={{ maxHeight: 'calc(100vh - 300px)' }}>
                  <style>{`
                  .custom-scrollbar::-webkit-scrollbar {
                    height: 10px;
                    width: 10px;
                  }
                  .custom-scrollbar::-webkit-scrollbar-track {
                    background: #F3F4F6;
                    border-radius: 4px;
                  }
                  .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #9CA3AF;
                    border-radius: 4px;
                  }
                  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #6B7280;
                  }
                  .custom-scrollbar {
                    scrollbar-width: thin;
                    scrollbar-color: #9CA3AF #F3F4F6;
                  }
                  /* Sticky header and first column */
                  .sticky-header {
                    position: sticky;
                    top: 0;
                    z-index: 10;
                    background: #F9FAFB;
                  }
                  .sticky-col {
                    position: sticky;
                    right: 0;
                    z-index: 5;
                    background: white;
                  }
                  .sticky-header.sticky-col {
                    z-index: 15;
                    background: #F9FAFB;
                  }
                  tbody tr:hover .sticky-col {
                    background: #F9FAFB;
                  }
                `}</style>
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr className="text-right">
                        <th className="sticky-header sticky-col px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider shadow-[inset_-1px_0_0_rgba(229,231,235,1)]">
                          ردیف
                        </th>
                        <th className="sticky-header px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          جزئیات
                        </th>
                        <th className="sticky-header px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          درخواست تمدید
                        </th>
                        <th className="sticky-header px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          شناسه درخواست
                        </th>
                        <th className="sticky-header px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          نام و نام خانوادگی
                        </th>
                        <th className="sticky-header px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          تاریخ ثبت
                        </th>
                        <th className="sticky-header px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          وضعیت
                        </th>
                        <th className="sticky-header px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          هزینه آزمایش
                        </th>
                        <th className="sticky-header px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          هزینه ایاب و ذهاب
                        </th>
                        <th className="sticky-header px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          مجموع پرداختی
                        </th>
                        <th className="sticky-header px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          دلیل رد
                        </th>
                        <th className="sticky-header px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          دستورالعمل‌ها
                        </th>
                        <th className="sticky-header px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          تاریخ آزمایش
                        </th>
                        <th className="sticky-header px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" title="نمایش مهلت پرداخت برای درخواست‌های تایید شده (90 دقیقه) و مهلت ویرایش برای درخواست‌های رد شده (24 ساعت)">
                          مهلت پرداخت / ویرایش
                        </th>
                        <th className="sticky-header px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          وضعیت پرداخت
                        </th>
                        <th className="sticky-header px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          تاریخ پرداخت
                        </th>
                        <th className="sticky-header px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          ساعت پرداخت
                        </th>
                        <th className="sticky-header px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          کد رهگیری
                        </th>
                        <th className="sticky-header px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          درگاه پرداخت
                        </th>
                        <th className="sticky-header px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          محل نمونه گیری
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {testRequests.map((request, index) => (
                        <tr
                          key={request._id}
                          className="hover:bg-gray-50 transition-colors cursor-pointer group"
                          onClick={(e) => {
                            // Don't navigate if clicking on the button
                            if ((e.target as HTMLElement).closest('a')) {
                              return;
                            }
                            window.location.href = `/test-requests/${request._id}`;
                          }}
                        >
                          <td className="sticky-col px-4 py-3 whitespace-nowrap text-sm text-gray-900 shadow-[inset_-1px_0_0_rgba(229,231,235,1)]">
                            {(pagination.currentPage - 1) * 10 + index + 1}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm bg-white group-hover:bg-gray-50">
                            <div className="flex items-center gap-2">
                              <Link
                                to={`/test-requests/${request._id}`}
                                className="inline-flex items-center justify-center px-3 py-1.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-md hover:bg-indigo-200 transition-colors"
                              >
                                جزئیات
                              </Link>
                              {request.status === 'rejected' && canEditRequest(request.editDeadline, request.status) && (
                                <Link
                                  to={`/edit-request/${request._id}`}
                                  className="inline-flex items-center justify-center px-3 py-1.5 bg-green-100 text-green-700 text-xs font-medium rounded-md hover:bg-green-200 transition-colors"
                                  title="ویرایش درخواست رد شده"
                                >
                                  ✏️ ویرایش
                                </Link>
                              )}
                              {request.status === 'incomplete' && (
                                <Link
                                  to={`/edit-request/${request._id}`}
                                  className="inline-flex items-center justify-center px-3 py-1.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-md hover:bg-orange-200 transition-colors"
                                  title="ادامه تکمیل درخواست"
                                >
                                  📝
                                </Link>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <div className="flex items-center gap-2">
                              {request.status === 'expired' && !request.paymentExtensionRequest && canRequestExtension(request.labResponseDateTime, request.status) && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRequestPaymentExtension(request._id);
                                  }}
                                  disabled={requestingExtension === request._id}
                                  className="inline-flex items-center justify-center px-3 py-1.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-md hover:bg-yellow-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="درخواست تمدید پرداخت"
                                >
                                  {requestingExtension === request._id ? '⏳' : '⏰'} تمدید پرداخت
                                </button>
                              )}
                              {/* Show "extension expired" message when 24h passed */}
                              {request.status === 'expired' && !request.paymentExtensionRequest && !canRequestExtension(request.labResponseDateTime, request.status) && (
                                <span className="inline-flex items-center justify-center px-3 py-1.5 bg-gray-100 text-gray-500 text-xs font-medium rounded-md">
                                  ⛔ مهلت تمدید تمام شده
                                </span>
                              )}

                              {(() => {
                                const extensionStatus = getPaymentExtensionStatusDisplay(request.paymentExtensionRequest, request.paymentExtensionStatus, request.paymentExtensionRejectionReason);
                                return extensionStatus && (
                                  <div className="flex flex-col gap-1">
                                    <span className={`inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-md ${extensionStatus.className}`}>
                                      {extensionStatus.text}
                                    </span>
                                    {extensionStatus.reason && (
                                      <div className="text-xs text-red-600 bg-red-50 p-2 rounded border border-red-200" title={extensionStatus.reason}>
                                        <strong>دلیل:</strong> {extensionStatus.reason.length > 50 ? extensionStatus.reason.substring(0, 50) + '...' : extensionStatus.reason}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-medium">
                            {request.displayId || request._id.slice(-8)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{request.patientInfo.firstName} {request.patientInfo.lastName}</div>
                            <div className="text-xs text-gray-500">{translatePatientType(request.patientInfo.patientType)}</div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {toShamsiDate(request.createdAt)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex px-2 py-1 text-xs rounded-full ${request.status === 'approved' ? 'bg-green-100 text-green-800' : request.status === 'rejected' ? 'bg-red-100 text-red-800' : request.status === 'paid' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                                {translateStatus(request.status, request)}
                              </span>
                              {request.editVersion && request.editVersion > 0 && request.editVersion != null && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800" title={`این درخواست ${request.editVersion} بار ویرایش شده است`}>
                                  {request.editVersion}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {request.amount !== undefined && request.amount !== null ? (request.amount === 0 ? 'رایگان' : `${new Intl.NumberFormat('fa-IR').format(request.amount)} ریال`) : '-'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {(() => {
                              if (request.transportationPrice !== undefined && request.transportationPrice !== null) {
                                return request.transportationPrice === 0
                                  ? 'رایگان'
                                  : `${new Intl.NumberFormat('fa-IR').format(request.transportationPrice)} ریال`;
                              }
                              return '-';
                            })()}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-medium">
                            {(() => {
                              const amount = request.amount || 0;
                              const transportation = request.transportationPrice || 0;
                              const total = amount + transportation;
                              return total === 0 ? 'رایگان' : `${new Intl.NumberFormat('fa-IR').format(total)} ریال`;
                            })()}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {request.rejectionReason ? (
                              <div className="max-w-xs text-xs text-red-600 truncate" title={request.rejectionReason.replace(/\n/g, '\n').replace(/•\s*/g, '• ')}>
                                <span className="inline-flex items-center">
                                  <span className="ml-1">❌</span>
                                  {request.rejectionReason.split('\n')[0].replace(/^•\s*/, '')}
                                  {request.rejectionReason.split('\n').length > 1 && '...'}
                                </span>
                              </div>
                            ) : (
                              <span className="text-gray-500">---</span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {request.instructions ? (
                              <div className="max-w-xs text-xs text-green-600 truncate" title={request.instructions.replace(/\n/g, '\n').replace(/•\s*/g, '• ')}>
                                <span className="inline-flex items-center">
                                  <span className="ml-1">📋</span>
                                  {request.instructions.split('\n')[0].replace(/^•\s*/, '')}
                                  {request.instructions.split('\n').length > 1 && '...'}
                                </span>
                              </div>
                            ) : (
                              <span className="text-gray-500">---</span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {request.samplingDate ? toShamsiDate(request.samplingDate) : '---'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {request.status === 'approved' ? (
                              <div className="text-xs">
                                {isPaymentDeadlinePassed(request.labResponseDateTime, request.status) ? (
                                  <span className="text-red-600 font-medium">
                                    ❌ منقضی شده
                                  </span>
                                ) : (
                                  <span className="text-orange-600 font-medium">
                                    💰 {calculateRemainingPaymentTime(request.labResponseDateTime, request.status)}
                                  </span>
                                )}
                              </div>
                            ) : request.status === 'rejected' && request.editDeadline ? (
                              <div className="text-xs">
                                {canEditRequest(request.editDeadline, request.status) ? (
                                  <span className="text-blue-600 font-medium">
                                    ✏️ {calculateRemainingEditTime(request.editDeadline, request.status)}
                                  </span>
                                ) : (
                                  <span className="text-red-600">
                                    ❌ منقضی شده
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-500">---</span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {request.status === 'paid' ? (
                              <span className="inline-flex px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                                موفق
                              </span>
                            ) : request.status === 'expired' ? (
                              <span className="inline-flex px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                                عدم پرداخت
                              </span>
                            ) : request.status === 'canceled' ? (
                              <span className="inline-flex px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                                انصراف
                              </span>
                            ) : request.transaction ? (
                              <span className={`inline-flex px-2 py-1 text-xs rounded-full ${request.transaction.status === 'completed' ? 'bg-green-100 text-green-800' : request.transaction.status === 'failed' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                {request.transaction.status === 'completed' ? 'موفق' :
                                  request.transaction.status === 'failed' ? 'ناموفق' : 'در انتظار پرداخت'}
                              </span>
                            ) : (
                              <span className="text-gray-500">---</span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 bg-white group-hover:bg-gray-50">
                            {(() => {
                              // Show payment date only for completed transactions
                              if (request.transaction?.status === 'completed' && request.transaction?.paymentDate) {
                                return toShamsiDate(request.transaction.paymentDate);
                              } else if (request.status === 'paid' && request.updatedAt) {
                                return toShamsiDate(request.updatedAt);
                              } else if (request.status === 'expired' && request.updatedAt) {
                                return toShamsiDate(request.updatedAt);
                              }
                              return '---';
                            })()}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 bg-white group-hover:bg-gray-50">
                            {(() => {
                              // Show payment time only for completed transactions
                              if (request.transaction?.status === 'completed' && request.transaction?.paymentDate) {
                                return formatTime(request.transaction.paymentDate);
                              } else if (request.status === 'paid' && request.updatedAt) {
                                return formatTime(request.updatedAt);
                              } else if (request.status === 'expired' && request.updatedAt) {
                                return formatTime(request.updatedAt);
                              }
                              return '---';
                            })()}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-mono bg-white group-hover:bg-gray-50">
                            {request.transaction?.status === 'completed' ? (request.transaction?.refId || '---') : '---'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 bg-white group-hover:bg-gray-50">
                            {(() => {
                              if (request.transaction?.status === 'completed') {
                                const gateway = request.transaction?.gateway;
                                return gateway === 'parsian' ? 'پارسیان' : gateway || '---';
                              }
                              return '---';
                            })()}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <div className="max-w-xs">
                              {formatSamplingLocation(request)}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="flex justify-center py-5">
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={pagination.currentPage === 1}
                        className="relative inline-flex items-center px-4 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        قبلی
                      </button>

                      {/* Page numbers */}
                      {Array.from({ length: pagination.pages }, (_, i) => i + 1)
                        .filter(page => page === 1 || page === pagination.pages ||
                          (page >= pagination.currentPage - 1 && page <= pagination.currentPage + 1))
                        .map((page, index, array) => {
                          // Add ellipsis
                          if (index > 0 && page - array[index - 1] > 1) {
                            return (
                              <React.Fragment key={`ellipsis-${page}`}>
                                <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                  ...
                                </span>
                                <button
                                  onClick={() => handlePageChange(page)}
                                  className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${pagination.currentPage === page ? 'bg-indigo-50 text-indigo-600 border-indigo-500 z-10' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                                >
                                  {page}
                                </button>
                              </React.Fragment>
                            );
                          }
                          return (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${pagination.currentPage === page ? 'bg-indigo-50 text-indigo-600 border-indigo-500 z-10' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                            >
                              {page}
                            </button>
                          );
                        })
                      }

                      <button
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={pagination.currentPage === pagination.pages}
                        className="relative inline-flex items-center px-4 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        بعدی
                      </button>
                    </nav>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Address Modal */}
      {addressModalOpen && selectedAddress && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setAddressModalOpen(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">آدرس کامل</h3>
              <button
                onClick={() => setAddressModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="space-y-3 text-right">
              {selectedAddress.region && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">منطقه:</span>
                  <span className="text-gray-600">{selectedAddress.region}</span>
                </div>
              )}
              {selectedAddress.field && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">میدان:</span>
                  <span className="text-gray-600">{selectedAddress.field}</span>
                </div>
              )}
              {selectedAddress.street && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">خیابان:</span>
                  <span className="text-gray-600">{selectedAddress.street}</span>
                </div>
              )}
              {selectedAddress.alley && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">کوچه:</span>
                  <span className="text-gray-600">{selectedAddress.alley}</span>
                </div>
              )}
              {selectedAddress.building && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">ساختمان:</span>
                  <span className="text-gray-600">{selectedAddress.building}</span>
                </div>
              )}
              {selectedAddress.unit && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">واحد:</span>
                  <span className="text-gray-600">{selectedAddress.unit}</span>
                </div>
              )}
              {selectedAddress.number && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">پلاک:</span>
                  <span className="text-gray-600">{selectedAddress.number}</span>
                </div>
              )}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="font-medium text-gray-700 mb-2">آدرس کامل:</div>
                <div className="text-gray-600 leading-relaxed">
                  {[
                    selectedAddress.region && `منطقه ${selectedAddress.region}`,
                    selectedAddress.field && `میدان ${selectedAddress.field}`,
                    selectedAddress.street && `خیابان ${selectedAddress.street}`,
                    selectedAddress.alley && `کوچه ${selectedAddress.alley}`,
                    selectedAddress.building && `ساختمان ${selectedAddress.building}`,
                    selectedAddress.unit && `واحد ${selectedAddress.unit}`,
                    selectedAddress.number && `پلاک ${selectedAddress.number}`
                  ].filter(Boolean).join('، ')}
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setAddressModalOpen(false)}
                className="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 transition-colors"
              >
                بستن
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default MyTests;
